import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  combinePhoneNumber,
  getDefaultCountryCode,
  PhoneCountryCodeMap,
  useKivunovaTranslation,
  HandledGraphQLMutationError,
  translateCustomerCreateConflict
} from "@kivunova/kivufrontendcommon";

import { CustomerType } from "../../../Graphql/enums/customerType";
import type {
  CreateCustomerInput,
  Customer
} from "../../../Graphql/types/customer";
import { useCreateCustomer } from "../../../hooks/api/useCustomer";
import {
  buildQuickCustomerSchema,
  QuickCustomerFormValues
} from "./quickCustomerSchema";

export interface QuickAddCustomerModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  /** When true, `storeIdForCreate` must be set before submit (company admin + order store). */
  requireStoreId: boolean;
  storeIdForCreate?: string | null;
  onCustomerCreated: (customer: Customer) => void;
}

const QuickAddCustomerModal: React.FC<QuickAddCustomerModalProps> = ({
  open,
  onClose,
  companyId,
  requireStoreId,
  storeIdForCreate,
  onCustomerCreated
}) => {
  const { t } = useKivunovaTranslation();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const createCustomer = useCreateCustomer();
  const [submitApiError, setSubmitApiError] = useState<string | null>(null);

  const schema = useMemo(() => buildQuickCustomerSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<QuickCustomerFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema as any),
    mode: "onChange",
    defaultValues: {
      name: "",
      type: CustomerType.INDIVIDUAL,
      tin: "",
      phone: "",
      phoneCountryCode: getDefaultCountryCode(),
      email: ""
    }
  });

  const customerType = useWatch({ control, name: "type" });

  useEffect(() => {
    if (!open) return;
    setSubmitApiError(null);
    reset({
      name: "",
      type: CustomerType.INDIVIDUAL,
      tin: "",
      phone: "",
      phoneCountryCode: getDefaultCountryCode(),
      email: ""
    });
  }, [open, reset]);

  const storeReady = !requireStoreId || Boolean(storeIdForCreate?.trim());

  const onSubmit: SubmitHandler<QuickCustomerFormValues> = async (data) => {
    if (!storeReady) return;

    const trimmedLocal = (data.phone ?? "").trim();
    const fullPhone = trimmedLocal
      ? combinePhoneNumber(
          data.phoneCountryCode as string,
          trimmedLocal,
          PhoneCountryCodeMap
        )
      : undefined;

    const input: CreateCustomerInput = {
      companyId,
      name: data.name.trim(),
      type: data.type as CustomerType,
      ...(fullPhone ? { phone: fullPhone } : {}),
      ...(data.email?.trim() ? { email: data.email.trim() } : {}),
      ...(data.type === CustomerType.COMPANY && data.tin?.trim()
        ? { tin: data.tin.trim() }
        : {}),
      ...(requireStoreId && storeIdForCreate?.trim()
        ? { storeId: storeIdForCreate.trim() }
        : {})
    };

    try {
      const res = await createCustomer.mutateAsync(input);
      const newCustomer = res?.createCustomer?.customer;
      if (newCustomer?.id) {
        onCustomerCreated(newCustomer);
        onClose();
      }
    } catch (error) {
      if (error instanceof HandledGraphQLMutationError && error.apiCode) {
        const msg = translateCustomerCreateConflict(t, error.apiCode);
        setSubmitApiError(
          msg ??
            t(
              "create-customer-failed",
              "Could not create customer. Please try again."
            )
        );
        return;
      }
    }
  };

  const busy = isSubmitting || createCustomer.isPending;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("quick-add-customer-title", "New customer")}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {!storeReady && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {t(
                "quick-add-customer-need-store",
                "Select a store for this order first."
              )}
            </Typography>
          )}

          {submitApiError ? (
            <Alert
              severity="error"
              onClose={() => setSubmitApiError(null)}
              sx={{ mb: 2 }}
            >
              {submitApiError}
            </Alert>
          ) : null}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("name", "Name")}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={busy}
                />
              )}
            />

            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("customer-type", "Customer type")}
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  disabled={busy}
                >
                  {Object.values(CustomerType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {customerType === CustomerType.COMPANY && (
              <Controller
                name="tin"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    label={t("tin-label", "TIN")}
                    error={!!errors.tin}
                    helperText={errors.tin?.message}
                    disabled={busy}
                  />
                )}
              />
            )}

            <Box
              sx={{
                display: "flex",
                gap: matchMD ? 1 : 2,
                flexDirection: matchMD ? "column" : "row",
                alignItems: "flex-start"
              }}
            >
              <Controller
                name="phoneCountryCode"
                control={control}
                render={({ field }) => (
                  <FormControl
                    sx={{
                      flex: matchMD ? 1 : "0 0 160px",
                      minWidth: matchMD ? "100%" : 140
                    }}
                    size={matchMD ? "small" : "medium"}
                    error={!!errors.phoneCountryCode}
                    disabled={busy}
                  >
                    <InputLabel id="qa-phone-code-label">
                      {t("code", "Code")}
                    </InputLabel>
                    <Select
                      {...field}
                      labelId="qa-phone-code-label"
                      label={t("code", "Code")}
                      value={field.value || getDefaultCountryCode()}
                    >
                      {Object.keys(PhoneCountryCodeMap).map((key) => (
                        <MenuItem key={key} value={key}>
                          {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.phoneCountryCode && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {errors.phoneCountryCode.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    label={t(
                      "phone-optional-label",
                      "Phone (optional if email)"
                    )}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    disabled={busy}
                  />
                )}
              />
            </Box>

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("email-optional-label", "Email (optional if phone)")}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={busy}
                />
              )}
            />
          </Box>
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: matchMD ? "column" : "row",
            gap: matchMD ? 1 : 2,
            px: 3,
            pb: 2,
            pt: 1
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={busy}
            disabled={!storeReady}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("quick-add-customer-save", "Create customer")}
          </LoadingButton>
          <LoadingButton
            variant="outlined"
            onClick={onClose}
            disabled={busy}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </LoadingButton>
        </Box>
      </form>
    </Dialog>
  );
};

export default QuickAddCustomerModal;
